/**
 * Convert a time preset id (today / yesterday / 24h / 7d / 30d / 3m / 6m / 12m)
 * into a concrete { from, to } pair of ISO timestamp strings.
 *
 * Mirrors the math in features/activity/components/EventsTimeFilter.js
 * so any page that needs to seed its initial timeRange from a default preset
 * gets the same window as a user clicking the same preset chip.
 *
 * Returns null for unknown presets (caller should leave range empty in that
 * case — e.g. 'custom' is meaningless without explicit from/to).
 */
export function presetToISORange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today':
      return { from: today.toISOString(), to: now.toISOString() };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const yEnd = new Date(y);
      yEnd.setHours(23, 59, 59, 999);
      return { from: y.toISOString(), to: yEnd.toISOString() };
    }
    case '24h':
      // A rolling day, not "today": at 9am, 'today' is nine quiet hours while
      // '24h' is a full day of traffic.
      return { from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(), to: now.toISOString() };
    case '7d':
      return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(), to: now.toISOString() };
    case '30d':
      return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(), to: now.toISOString() };
    case '3m':
      return { from: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString(), to: now.toISOString() };
    case '6m':
      return { from: new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString(), to: now.toISOString() };
    case '12m':
      return { from: new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString(), to: now.toISOString() };
    default:
      return null;
  }
}

/** Default preset for every "list this stuff" page (Agent Runs, Traces,
 *  Events, Users, Sessions). 30 days covers most real usage, and pagination
 *  caps fetched rows at PAGE_SIZE so the cost stays the same as 'today'. */
export const DEFAULT_LIST_PRESET = '30d';
